/**
 * RequireFeature — gate any subtree on a per-tenant feature flag.
 *
 * Usage:
 *   <RequireFeature flag="modules.flows">
 *     <FlowsDashboardPage />
 *   </RequireFeature>
 *
 * If the flag is off:
 *   - returns `fallback` if provided (e.g. an empty-state page),
 *   - else navigates to "/" so unreachable URLs don't 404 mid-app.
 *
 * The only "loading" handling here is `isLoading` from auth: while the
 * auth context is still resolving, render `null` (ProtectedRoute is
 * showing LoadingScreen at the same time). Once auth is done, render
 * according to the resolved feature flag — even if `user.features` is
 * temporarily missing, fall through to the off-fallback rather than
 * leaving the page blank indefinitely.
 */
import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

interface RequireFeatureProps {
  flag: string;
  children: ReactNode;
  fallback?: ReactNode;
  /** Gate on the flag being OFF instead of ON — for legacy surfaces that
   *  retire when their replacement module is enabled (e.g. Data Bundles
   *  disappears once modules.library is on). */
  invert?: boolean;
}

export function RequireFeature({
  flag, children, fallback, invert,
}: RequireFeatureProps) {
  const { user, isLoading } = useAuth();

  if (isLoading) return null;   // ProtectedRoute shows LoadingScreen here
  if (!user?.features) return null;
  const enabled = !!user.features[flag];
  const pass = invert ? !enabled : enabled;
  if (!pass) return fallback !== undefined ? <>{fallback}</> : <Navigate to="/" replace />;
  return <>{children}</>;
}
